// Models
import { createIdentitySet } from "../../../utilities/identity";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
// Utilities
import cloneDeep from "lodash/cloneDeep";
import { Temporal } from "temporal-polyfill-lite";
import { validateSprintDates } from "./SprintUtilities";
/**
 * Planner Sprint entity implementation.
 */ export class Sprint extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, _source) {
        return {
            displayName: changed.displayName,
            startDate: changed.startDate !== undefined ? this.startDate.toString() : undefined,
            finishDate: changed.finishDate !== undefined ? this.finishDate.toString() : undefined,
            extensions: changed.extensions !== undefined ? cloneDeep(this.extensions) : undefined
        };
    }
    mapDeltaChangesToEntity(delta) {
        let startDate;
        let finishDate;
        const changes = this.processDeltaWithTranslators(delta, {
            startDate: (value)=>startDate = Temporal.PlainDate.from(value),
            finishDate: (value)=>finishDate = Temporal.PlainDate.from(value),
            extensions: (value)=>cloneDeep(value),
            archivalInfo: (deltaValue)=>({
                    isArchived: deltaValue.isArchived,
                    justification: deltaValue.justification,
                    modifiedBy: deltaValue.modifiedBy === undefined ? undefined : deltaValue.modifiedBy ? createIdentitySet(deltaValue.modifiedBy) : null,
                    modifiedDate: deltaValue.modifiedDate === undefined ? undefined : deltaValue.modifiedDate ? Temporal.PlainDate.from(deltaValue.modifiedDate) : null
                }),
            createdBy: (value)=>createIdentitySet(value),
            createdDateTime: (value)=>Temporal.Instant.from(value),
            lastModifiedBy: (value)=>createIdentitySet(value),
            lastModifiedDateTime: (value)=>Temporal.Instant.from(value)
        });
        validateSprintDates(startDate ?? this.startDate, finishDate ?? this.finishDate);
        return changes;
    }
    /**
     * Creates a Sprint from a PlannerService resource.
     */ static fromResource(resource) {
        return new Sprint({
            id: resource.id,
            version: resource["@etag"],
            planId: resource.planId,
            displayName: resource.displayName,
            startDate: Temporal.PlainDate.from(resource.startDate),
            finishDate: Temporal.PlainDate.from(resource.finishDate),
            extensions: cloneDeep(resource.extensions),
            archivalInfo: {
                isArchived: resource.archivalInfo.isArchived,
                justification: resource.archivalInfo.justification,
                modifiedBy: resource.archivalInfo.modifiedBy ? createIdentitySet(resource.archivalInfo.modifiedBy) : null,
                modifiedDate: resource.archivalInfo.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            createdBy: createIdentitySet(resource.createdBy),
            createdDateTime: Temporal.Instant.from(resource.createdDateTime),
            lastModifiedBy: createIdentitySet(resource.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime)
        });
    }
    getClone() {
        return new Sprint({
            id: this.id,
            version: this.version,
            planId: this.planId,
            displayName: this.displayName,
            startDate: Temporal.PlainDate.from(this.startDate),
            finishDate: Temporal.PlainDate.from(this.finishDate),
            extensions: cloneDeep(this.extensions),
            archivalInfo: Sprint.cloneArchivalInfo(this.archivalInfo),
            createdBy: cloneDeep(this.createdBy),
            createdDateTime: Temporal.Instant.from(this.createdDateTime),
            lastModifiedBy: cloneDeep(this.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(this.lastModifiedDateTime)
        });
    }
    setProperty(key, value) {
        const properties = {
            id: this.id,
            version: this.version,
            planId: this.planId,
            displayName: this.displayName,
            startDate: this.startDate,
            finishDate: this.finishDate,
            extensions: this.extensions,
            archivalInfo: this.archivalInfo,
            createdBy: this.createdBy,
            createdDateTime: this.createdDateTime,
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime,
            [key]: value
        };
        return new Sprint(properties);
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            planId: this.planId,
            displayName: this.displayName,
            startDate: this.startDate.toString(),
            finishDate: this.finishDate.toString(),
            extensions: cloneDeep(this.extensions),
            archivalInfo: {
                isArchived: this.archivalInfo.isArchived,
                justification: this.archivalInfo.justification,
                modifiedBy: this.archivalInfo.modifiedBy ? cloneDeep(this.archivalInfo.modifiedBy) : null,
                modifiedDate: this.archivalInfo.modifiedDate?.toString() ?? null
            },
            createdBy: cloneDeep(this.createdBy),
            createdDateTime: this.createdDateTime.toString(),
            lastModifiedBy: cloneDeep(this.lastModifiedBy),
            lastModifiedDateTime: this.lastModifiedDateTime.toString()
        };
    }
    forCreate() {
        return {
            planId: this.planId,
            displayName: this.displayName,
            startDate: this.startDate.toString(),
            finishDate: this.finishDate.toString(),
            extensions: cloneDeep(this.extensions)
        };
    }
    static validatePlanId(planId) {
        if (planId.trim().length === 0) {
            throw new Error("Invalid planId: planId is required.");
        }
    }
    static cloneArchivalInfo(archivalInfo) {
        return {
            isArchived: archivalInfo.isArchived,
            justification: archivalInfo.justification,
            modifiedBy: archivalInfo.modifiedBy ? cloneDeep(archivalInfo.modifiedBy) : null,
            modifiedDate: archivalInfo.modifiedDate ? Temporal.PlainDate.from(archivalInfo.modifiedDate) : null
        };
    }
    constructor(propertyBag){
        super(propertyBag);
        Sprint.validatePlanId(propertyBag.planId);
        validateSprintDates(propertyBag.startDate, propertyBag.finishDate);
        this.planId = propertyBag.planId;
        this.displayName = propertyBag.displayName;
        this.startDate = Temporal.PlainDate.from(propertyBag.startDate);
        this.finishDate = Temporal.PlainDate.from(propertyBag.finishDate);
        this.extensions = cloneDeep(propertyBag.extensions ?? {});
        this.archivalInfo = Sprint.cloneArchivalInfo(propertyBag.archivalInfo ?? {
            isArchived: false,
            justification: null,
            modifiedBy: null,
            modifiedDate: null
        });
        this.createdBy = cloneDeep(propertyBag.createdBy);
        this.createdDateTime = propertyBag.createdDateTime ? Temporal.Instant.from(propertyBag.createdDateTime) : Temporal.Now.instant();
        this.lastModifiedBy = cloneDeep(propertyBag.lastModifiedBy);
        this.lastModifiedDateTime = propertyBag.lastModifiedDateTime ? Temporal.Instant.from(propertyBag.lastModifiedDateTime) : Temporal.Now.instant();
    }
}
